import { useMemo } from 'react';
import Loader from '@/components/common/Loader';
import type { OrgContractDocumentItem } from '@/modules/org/contracts/types/contract-document.types';
import { ReviewMessage } from '@/modules/templates/components/review/ReviewMessage';
import DocxEditableReview from './DocxEditableReview';
import { parseDocxDocument } from './document-payload';
import { useEditableDocxBytes } from './useEditableDocxBytes';

/**
 * Resolves the .docx bytes before mounting the editable review.
 *
 * This wrapper exists for a real reason rather than tidiness: the review seeds
 * its undo/redo history from `initialDocxBytes` at mount, so it must not be
 * mounted until the bytes actually exist.
 */
export default function DocxEditableBranch({
  contract,
  documentId,
  slug,
  hideDocumentActions = false,
}: {
  contract: OrgContractDocumentItem;
  documentId: string;
  slug: string;
  hideDocumentActions?: boolean;
}) {
  const document = useMemo(() => parseDocxDocument(contract.Document), [contract.Document]);
  const { bytes, error, isLoading } = useEditableDocxBytes({ contract, document, slug });

  if (error) return <ReviewMessage>{error}</ReviewMessage>;
  if (isLoading || !bytes) return <Loader />;

  return (
    <DocxEditableReview
      contract={contract}
      documentId={documentId}
      slug={slug}
      initialDocxBytes={bytes}
      hideDocumentActions={hideDocumentActions}
    />
  );
}
